import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { AlertTriangle, Clock, LogOut, RefreshCw } from 'lucide-react';
import LoginPage from './pages/LoginPage'; // Adjust path as needed
import './styles/App.css';
import { useAuth } from './context/AuthContext';

// Constants for timing
const SESSION_CHECK_INTERVAL = 1000 * 60 * 1; // Check session every 1 minute
const WARNING_BEFORE_TIMEOUT = 1000 * 60 * 30; // Show warning 30 minutes before timeout
const COUNTDOWN_DURATION = 60; // 60 seconds countdown
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

// TESTING VARIABLES - set to true to enable testing mode
const TESTING_MODE = {
  enabled: false,                   // Master switch for testing mode
  showTimeoutImmediately: false,   // Show timeout modal immediately on component mount
  simulateTimeout: false,          // Simulate timeout after delay
  timeoutDelay: 3000,              // Delay before showing timeout modal (in ms)
  shortCountdown: true,            // Use a shorter countdown for testing
  testCountdownDuration: 30,       // Test countdown duration in seconds
  shortWarningTime: 1000 * 10      // 10 seconds for testing (instead of 30 minutes)
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // User state
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [initialAuthCheckComplete, setInitialAuthCheckComplete] = useState(false);
  
  // Modal states
  const [isTimeoutModalOpen, setIsTimeoutModalOpen] = useState(false);
  const [countdown, setCountdown] = useState(
    TESTING_MODE.shortCountdown ? TESTING_MODE.testCountdownDuration : COUNTDOWN_DURATION
  );

  // Refs to store timer IDs
  const sessionCheckIntervalRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Check if we're on the password reset page
  const isPasswordResetPage = location.pathname.startsWith('/reset_password/');
  const isLoginPage = location.pathname.startsWith('/login');

  // User authentication functions
  const { refreshUser, logout, getCookie } = useAuth();

  // Reset the warning timer
  const resetWarningTimer = useCallback(() => {
    // Clear existing warning timer
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }

    // Don't set new timer if modal is already open or we're on reset page
    if (isTimeoutModalOpen || isPasswordResetPage || TESTING_MODE.enabled) {
      return;
    }

    // Set new warning timer
    const warningDelay = TESTING_MODE.enabled ? TESTING_MODE.shortWarningTime : WARNING_BEFORE_TIMEOUT;
    warningTimeoutRef.current = setTimeout(() => {
      // console.log('⚠️ Session warning timeout reached');
      startCountdown();
    }, warningDelay);
  }, [isTimeoutModalOpen, isPasswordResetPage]);

  // Track user activity
  const handleUserActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    // Reset warning timer on activity (but only if modal isn't open)
    if (!isTimeoutModalOpen) {
      resetWarningTimer();
    }
  }, [resetWarningTimer, isTimeoutModalOpen]);

  // Timeout modal handlers
  const startCountdown = useCallback(() => {
    // console.log('🚨 Starting session timeout countdown');
    setCountdown(
      TESTING_MODE.shortCountdown ? TESTING_MODE.testCountdownDuration : COUNTDOWN_DURATION
    );
    setIsTimeoutModalOpen(true);
    
    // Clear the warning timer since modal is now open
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
  }, []);

  const handleLogout = useCallback(() => {
    console.log('👋 Logging out user');
    
    // Clear all timers
    if (sessionCheckIntervalRef.current) {
      clearInterval(sessionCheckIntervalRef.current);
      sessionCheckIntervalRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    
    logout().then(() => {
      navigate("/login");
    });
  }, [logout, navigate]);

  const handleStayLoggedIn = useCallback(() => {
    // console.log('✅ User chose to stay logged in');
    
    refreshUser().then((loggedIn) => {
      if (loggedIn) {
        console.log('✅ Session refresh successful');
        setIsTimeoutModalOpen(false);
        // Reset the warning timer for another cycle
        resetWarningTimer();
      } else {
        console.log('❌ Session refresh failed - logging out');
        document.cookie = '';
        navigate('/login');
      }
    });
  }, [refreshUser, navigate, resetWarningTimer]);

  // Initial auth check
  useEffect(() => {
    const performInitialAuthCheck = async () => {
      if (isPasswordResetPage) {
        setIsLoading(false);
        setInitialAuthCheckComplete(true);
        return;
      }

      try {
        console.log('🔍 Initial auth check');
        
        // Check if we have authentication tokens first
        const accessToken = getCookie('csrf_access_token');
        const refreshToken = getCookie('csrf_refresh_token');
        
        if (!accessToken && !refreshToken) {
          console.log('❌ No authentication tokens found');
          setIsAuthenticated(false);
          setUser(null);
          if (!isLoginPage) {
            navigate('/login');
          }
          return;
        }

        // Only make API call if we have tokens
        const loggedIn = await refreshUser();
        
        if (!loggedIn) {
          console.log('❌ Not logged in');
          document.cookie = '';
          setIsAuthenticated(false);
          setUser(null);
          if (!isLoginPage) {
            navigate('/login');
          }
        } else {
          console.log('✅ Initial auth check passed');
          setIsAuthenticated(true);
          if (isLoginPage) {
            navigate('/');
          }
        }
      } catch (error) {
        console.error('Initial auth check error:', error);
        setIsAuthenticated(false);
        setUser(null);
        if (!isLoginPage) {
          navigate('/login');
        }
      } finally {
        setIsLoading(false);
        setInitialAuthCheckComplete(true);
      }
    };

    // Only run once
    if (!initialAuthCheckComplete) {
      performInitialAuthCheck();
    }
  }, [isPasswordResetPage, isLoginPage, getCookie, refreshUser, navigate, initialAuthCheckComplete]);

  // Set up activity listeners
  useEffect(() => {
    if (isPasswordResetPage || !isAuthenticated) return;

    // Add activity listeners
    ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, handleUserActivity, true);
    });

    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        document.removeEventListener(event, handleUserActivity, true);
      });
    };
  }, [handleUserActivity, isPasswordResetPage, isAuthenticated]);

  // Regular session checking and timeout warning
  useEffect(() => {
    if (isPasswordResetPage || TESTING_MODE.enabled || !isAuthenticated) return;

    // Regular session refresh
    sessionCheckIntervalRef.current = setInterval(async () => {
      console.log('🔍 Performing regular session check');
      
      try {
        // Check if we still have tokens before making API call
        const accessToken = getCookie('csrf_access_token');
        const refreshToken = getCookie('csrf_refresh_token');
        
        if (!accessToken && !refreshToken) {
          console.log('❌ No authentication tokens found during session check');
          setIsAuthenticated(false);
          setUser(null);
          navigate('/login');
          return;
        }

        const loggedIn = await refreshUser();
        
        if (!loggedIn) {
          console.log('❌ Session check failed - logging out');
          document.cookie = '';
          setIsAuthenticated(false);
          setUser(null);
          navigate('/login');
        } else {
          setIsAuthenticated(true);
          console.log('✅ Session check passed');
        }
      } catch (error) {
        console.error('Session check error:', error);
        // Don't immediately log out on network errors during regular checks
        // Let the user continue and handle it on next interaction
      }
    }, SESSION_CHECK_INTERVAL);

    // Set initial warning timer
    resetWarningTimer();

    return () => {
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
        sessionCheckIntervalRef.current = null;
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
        warningTimeoutRef.current = null;
      }
    };
  }, [isPasswordResetPage, isAuthenticated, getCookie, refreshUser, navigate, resetWarningTimer]);

  // Countdown timer effect
  useEffect(() => {
    if (isTimeoutModalOpen && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (isTimeoutModalOpen && countdown === 0) {
      console.log('⏰ Countdown reached zero - logging out');
      handleLogout();
    }
  }, [isTimeoutModalOpen, countdown, handleLogout]);

  // TESTING: Simulate timeout immediately or after delay
  useEffect(() => {
    if (!TESTING_MODE.enabled) return;
    
    console.log('🧪 Testing mode enabled');
    
    // Show timeout modal immediately or after delay based on testing settings
    if (TESTING_MODE.showTimeoutImmediately) {
      startCountdown();
    } else if (TESTING_MODE.simulateTimeout) {
      const testTimeout = setTimeout(() => {
        startCountdown();
      }, TESTING_MODE.timeoutDelay);
      return () => clearTimeout(testTimeout);
    }
  }, [startCountdown]);

  // Format the countdown time nicely (MM:SS)
  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Show loading state
  if (isLoading) {
    return null;
  }

  // Main app content
  return (
    <div className="App">
      <Outlet/>
      
      {/* Session Timeout Modal */}
      {isTimeoutModalOpen && (
        <div className="session-timeout-overlay">
          <div className="session-timeout-modal">
            <div className="session-timeout-header">
              <h2 className="session-timeout-title">
                <AlertTriangle className="h-6 w-6 text-amber-500" />
                Session Timeout Warning
              </h2>
            </div>
            <div className="session-timeout-content">
              <div className="session-timeout-message">
                <Clock className="h-5 w-5 text-blue-500" />
                Your session is about to expire due to inactivity.
              </div>
              <p className="session-timeout-description">
                For security reasons, you will be automatically logged out in{' '}
                <span 
                  className={`session-timeout-countdown ${countdown < 10 ? 'countdown-warning' : ''}`}
                >
                  {formatCountdown(countdown)}
                </span>
                {' '}unless you choose to stay logged in.
              </p>
              <div className="session-timeout-buttons">
                <button 
                  onClick={handleLogout} 
                  className="logout-button"
                >
                  <LogOut className="h-4 w-4" />
                  Logout Now
                </button>
                <button 
                  onClick={handleStayLoggedIn} 
                  className="stay-button"
                  autoFocus
                >
                  <RefreshCw className="h-4 w-4" />
                  Stay Logged In
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Testing Controls */}
      {TESTING_MODE.enabled && (
        <div className="testing-controls">
          <h4>Testing Controls</h4>
          <button 
            onClick={startCountdown}
            className="testing-button"
          >
            Show Timeout Modal
          </button>
          <div className="testing-info">
            <p>Last Activity: {new Date(lastActivityRef.current).toLocaleTimeString()}</p>
            <p>Modal Open: {isTimeoutModalOpen ? 'Yes' : 'No'}</p>
            <p>Authenticated: {isAuthenticated ? 'Yes' : 'No'}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;